/** Create/edit dialog for a delivery blacklist rule (console).
 *
 * Two tabs: Editor and Change history. A non-empty reason is mandatory for every
 * save; deletion (with its own reason) is handled from the grid.
 *
 * The user match type is a bare numeric id on the wire and in the form, which
 * is unreadable; the saved rule's payload carries the account, so the resolved
 * email is rendered under the field as a trigger for that user's page and
 * bookings. The pickers keep their click throughout — links hang off the
 * picked value, never off the input.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert, Autocomplete, Box, Button, ButtonBase, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, InputLabel,
  MenuItem, Select, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
  TextField, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { blacklistsApi, type BlacklistRuleWriteInput, type OtaBrief } from '@/api/blacklists';
import { ConsoleApiError } from '@/api/client';
import { eventGroupsApi } from '@/api/eventGroups';
import { blacklistTagLookups } from '@/api/tagLookups';
import { FieldHelp, labelWithHelp, type FieldHelpEntry } from '@/components/FieldHelp';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import {
  EMPTY_TAG_CONDITION,
  fromWireChips,
  TagConditionInput,
  TagSourcesSelect,
  toWireChips,
  type TagConditionFetchers,
  type TagConditionValue,
} from '@/components/TagConditionInput';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { actorActions, ruleOtaActions, ruleUserActions } from './blacklistLinks';

// Mirror of Notification_Blacklist_Rule.REDIRECT_MAX_INBOXES / the 2048-char
// column cap enforced in backend apiv2/lib/blacklist.py apply_payload.
const MAX_REDIRECT_INBOXES = 5;
const MAX_REDIRECT_JOINED_LENGTH = 2048;

const splitInboxes = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);
const emailOk = (v: string) => z.string().email().safeParse(v).success;

// The widget never imports API modules; bind it to the blacklist resource's own
// tag lookups (reachable with console_blacklist_view).
const TAG_FETCHERS: TagConditionFetchers = {
  fetchTenantCategories: (tenantId, q, signal) =>
    blacklistTagLookups.tenantCategories(tenantId, q, signal),
  fetchAllCategoryCodes: (q, signal) => blacklistTagLookups.categoryCodes(q, signal),
  fetchCategoryTags: (categoryId, q, signal) =>
    blacklistTagLookups.categoryTags(categoryId, q, signal),
  fetchTagsForCode: (code, q, signal) => blacklistTagLookups.tagsForCode(code, q, signal),
  fetchCanonicalsForCode: (code, q, signal) => blacklistTagLookups.canonicalsForCode(code, q, signal),
};

const TAG_HELP = {
  condition: {
    title: 'Tag condition',
    body: 'Blocks every delivery of a booking whose context carries these tags. '
      + 'A chip is either pinned to one tenant\'s exact tag (#) or matches by '
      + 'category code + tag name in whichever tenant is delivering (dashed). '
      + 'AND requires every chip, OR any one of them.',
    story: 'notification-blacklists',
  },
  sources: {
    title: 'Tag sources',
    body: 'Which entities\' tags count as present in the context. The pool is the '
      + 'UNION of the selected sources, so adding one can only make the rule match — '
      + 'and therefore block — more often. Booking (the participant) is the default.',
    story: 'notification-blacklists',
  },
} satisfies Record<string, FieldHelpEntry>;

const REDIRECT_HELP = {
  redirect_to: {
    title: 'Redirect blocked deliveries to',
    body: 'Instead of dropping a blocked delivery silently, email the rendered message '
      + '(plus the intended destination and block reason) to these inboxes. '
      + `Up to ${MAX_REDIRECT_INBOXES} comma-separated addresses; each gets its own copy.`,
    story: 'blacklist-redirects',
  },
  reply_to: {
    title: 'Custom Reply-To email',
    body: 'Replies from the redirect inbox(es) go to this address — e.g. the operator\'s '
      + 'own support inbox — instead of being routed into the guest\'s chat conversation. '
      + 'Setting it supersedes the reply-into-conversation switch.',
    story: 'blacklist-redirects',
  },
  append_sid: {
    title: 'Reply into the guest conversation',
    body: 'Tags the redirect email so a plain reply from the inbox lands in the guest\'s '
      + 'in-app chat, attributed to the guest. Only senders configured as redirect '
      + 'inboxes are trusted for this.',
    story: 'blacklist-redirects',
  },
} satisfies Record<string, FieldHelpEntry>;

const Schema = z.object({
  delivery_type: z.enum(['email', 'sms', 'whatsapp', 'airbnb']),
  match_type: z.enum(['exact', 'substring', 'regex', 'user', 'ota', 'reseller', 'tag']),
  pattern: z.string().optional().default(''),
  user: z.string().optional().default(''),
  block_all_events: z.boolean().default(true),
  event_codes: z.array(z.string()).default([]),
  event_group_ids: z.array(z.number()).default([]),
  exception_event_codes: z.array(z.string()).default([]),
  exception_event_group_ids: z.array(z.number()).default([]),
  comment: z.string().optional().default(''),
  is_active: z.boolean().default(true),
  redirect_to_email: z.string().default('').superRefine((v, ctx) => {
    const addrs = splitInboxes(v);
    if (addrs.length > MAX_REDIRECT_INBOXES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `At most ${MAX_REDIRECT_INBOXES} redirect email addresses are allowed.`,
      });
      return;
    }
    const bad = addrs.find((a) => !emailOk(a));
    if (bad !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Not a valid email address: ${bad}` });
      return;
    }
    if (addrs.join(', ').length > MAX_REDIRECT_JOINED_LENGTH) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'The redirect address list is too long.' });
    }
  }),
  redirect_reply_to_email: z.string().default('').superRefine((v, ctx) => {
    const t = v.trim();
    if (!t) return;
    if (t.includes(',')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Only one Reply-To email address is allowed.' });
      return;
    }
    if (!emailOk(t)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Not a valid email address.' });
    }
  }),
  redirect_append_conversation_sid: z.boolean().default(true),
  reason: z.string().trim().min(1, 'A reason is required'),
});
type Input = z.infer<typeof Schema>;

interface Props {
  ruleId: number | null; // null = create
  /** The page's operators (D9), for create: a page that names any opens on the
   *  Tenant scope, with its one operator pre-selected and all of them featured;
   *  none opens on System. Without it the picker follows the console scope. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
  onSaved: () => void;
}

/** Divider-style section header that toggles its body. Collapsed by default;
 *  shows a one-line summary of what's configured while collapsed, and is
 *  forced open while it contains a validation error (a collapsed section must
 *  never hide what blocks the save). */
function CollapsibleSection({ title, summary, forceOpen = false, children }: {
  title: string;
  summary: string | null;
  forceOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const expanded = open || forceOpen;
  return (
    <Box>
      <Divider textAlign="left">
        <ButtonBase
          onClick={() => setOpen((o) => !o)}
          disableRipple
          aria-expanded={expanded}
          sx={{ gap: 0.5, borderRadius: 1, typography: 'body2' }}
        >
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: expanded ? 'none' : 'rotate(-90deg)',
              transition: 'transform 150ms',
            }}
          />
          {title}
        </ButtonBase>
      </Divider>
      {!expanded && summary && (
        <Typography variant="caption" color="text.secondary" noWrap
          sx={{ display: 'block', mt: 0.5, pl: 4.5 }}>
          {summary}
        </Typography>
      )}
      <Collapse in={expanded}>
        <Stack spacing={2} sx={{ pt: 2 }}>{children}</Stack>
      </Collapse>
    </Box>
  );
}

export function BlacklistRuleDialog({ ruleId, pageTenants, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const isEdit = ruleId !== null;
  const [tab, setTab] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);

  // Create: the tenant (mounted per open, so seeded on every open) and the
  // system-vs-tenant scope it starts on.
  const [tenant, setTenant] = useCreateDialogTenant(true, pageTenants);
  const [scope, setScope] = useState<'system' | 'tenant'>(() =>
    (pageTenants !== undefined ? pageTenants.length > 0 : tenant !== null) ? 'tenant' : 'system',
  );
  const [ota, setOta] = useState<OtaBrief | null>(null);

  const ruleQuery = useQuery({
    queryKey: ['consoleBlacklistRule', ruleId],
    queryFn: () => blacklistsApi.retrieve(ruleId as number),
    enabled: isEdit,
  });
  const groupsQuery = useQuery({
    queryKey: ['consoleEventGroupsAll'],
    queryFn: () => eventGroupsApi.list({ page: 1, page_size: 500 }),
  });
  const codesQuery = useQuery({
    queryKey: ['consoleBlacklistEventCodes'],
    queryFn: () => blacklistsApi.eventCodes(),
  });

  const [tagCondition, setTagCondition] = useState<TagConditionValue>(EMPTY_TAG_CONDITION);
  const [tagSources, setTagSources] = useState<string[]>(['booking']);
  const tagRegistryQuery = useQuery({
    queryKey: ['consoleBlacklistTagSources'],
    queryFn: () => blacklistsApi.tagSources(),
  });

  const { control, register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } =
    useForm<Input>({
      resolver: zodResolver(Schema),
      defaultValues: {
        delivery_type: 'email', match_type: 'exact', pattern: '', user: '',
        block_all_events: true, event_codes: [], event_group_ids: [],
        exception_event_codes: [], exception_event_group_ids: [],
        comment: '', is_active: true, redirect_to_email: '',
        redirect_reply_to_email: '', redirect_append_conversation_sid: true, reason: '',
      },
    });

  // seed the form when editing (once loaded)
  useEffect(() => {
    if (!isEdit || !ruleQuery.data) return;
    const d = ruleQuery.data;
    setOta(d.ota);
    setTagCondition(fromWireChips(d.tags, d.tag_match_mode));
    // 'booking' default only for a rule that has no stored selection yet.
    setTagSources(d.tag_sources && d.tag_sources.length > 0 ? d.tag_sources : ['booking']);
    reset({
      delivery_type: d.delivery_type,
      match_type: d.match_type,
      pattern: d.pattern ?? '',
      user: d.user ? String(d.user.id) : '',
      block_all_events: d.block_all_events,
      event_codes: d.event_codes,
      event_group_ids: d.event_groups.map((g) => g.id),
      exception_event_codes: d.exception_event_codes,
      exception_event_group_ids: d.exception_event_groups.map((g) => g.id),
      comment: d.comment ?? '',
      is_active: d.is_active,
      redirect_to_email: d.redirect_to_email ?? '',
      redirect_reply_to_email: d.redirect_reply_to_email ?? '',
      redirect_append_conversation_sid: d.redirect_append_conversation_sid !== false,
      reason: '',
    });
  }, [isEdit, ruleQuery.data, reset]);

  // Links are scoped to the rule's own operator and named from the SAVED
  // record — the form only carries the user's id.
  const ruleTenantId = ruleQuery.data?.tour_operator?.id ?? null;
  const savedUser = ruleQuery.data?.user ?? null;

  const matchType = watch('match_type');
  const blockAll = watch('block_all_events');
  const redirectTo = watch('redirect_to_email');
  const redirectReplyTo = watch('redirect_reply_to_email');
  const appendSid = watch('redirect_append_conversation_sid');
  const excCodes = watch('exception_event_codes');
  const excGroupIds = watch('exception_event_group_ids');

  // Collapsed-state summaries (null = nothing configured, no summary shown).
  // Group names + event codes spelled out; the noWrap summary line ellipsizes overflow.
  const exceptionsSummary = excCodes.length + excGroupIds.length > 0
    ? [
        ...excGroupIds.map((id) => groupsQuery.data?.results.find((g) => g.id === id)?.name ?? `#${id}`),
        ...excCodes,
      ].join(', ')
    : null;
  const redirectInboxes = splitInboxes(redirectTo);
  const [firstInbox = ''] = redirectInboxes;
  const redirectSummary = redirectInboxes.length > 0
    ? `${firstInbox}${redirectInboxes.length > 1 ? ` +${redirectInboxes.length - 1} more` : ''} · ${
        redirectReplyTo.trim()
          ? `replies → ${redirectReplyTo.trim()}`
          : appendSid ? 'replies → guest chat' : 'replies not routed'}`
    : null;

  const mutation = useMutation({
    mutationFn: (values: Input) => {
      const isUser = values.match_type === 'user';
      const isOta = values.match_type === 'ota';
      const isTag = values.match_type === 'tag';
      const body: BlacklistRuleWriteInput = {
        delivery_type: values.delivery_type,
        match_type: values.match_type,
        pattern: (isUser || isOta || isTag) ? null : values.pattern.trim(),
        user: isUser ? Number(values.user) : null,
        ota: isOta ? ota?.id ?? null : null,
        ...(isTag ? {
          tags: toWireChips(tagCondition),
          tag_match_mode: tagCondition.matchMode,
          tag_sources: tagSources,
        } : {}),
        block_all_events: values.block_all_events,
        event_codes: values.block_all_events ? [] : values.event_codes,
        event_group_ids: values.block_all_events ? [] : values.event_group_ids,
        exception_event_codes: values.exception_event_codes,
        exception_event_group_ids: values.exception_event_group_ids,
        comment: values.comment.trim() || null,
        is_active: values.is_active,
        redirect_to_email: values.redirect_to_email.trim() || null,
        redirect_reply_to_email: values.redirect_reply_to_email.trim() || null,
        redirect_append_conversation_sid: values.redirect_append_conversation_sid,
        reason: values.reason.trim(),
      };
      if (isEdit) return blacklistsApi.update(ruleId as number, body);
      body.tour_operator = scope === 'system' ? 'system' : tenant?.id ?? null;
      return blacklistsApi.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consoleBlacklistRules'] });
      snackbar.show(isEdit ? 'Rule updated' : 'Rule created', 'success');
      onSaved();
    },
    onError: (e: unknown) =>
      setServerError(e instanceof ConsoleApiError ? e.message : 'Save failed'),
  });

  const onSubmit = handleSubmit((values) => {
    setServerError(null);
    if (values.match_type === 'user') {
      if (!values.user.trim()) { setServerError('A user id is required for the user match type.'); return; }
    } else if (values.match_type === 'ota') {
      if (!ota) { setServerError('An OTA is required for the OTA match type.'); return; }
    } else if (values.match_type === 'tag') {
      if (tagCondition.chips.length === 0) { setServerError('Add at least one tag to the condition.'); return; }
      if (tagSources.length === 0) { setServerError('Pick at least one tag source.'); return; }
    } else if (!values.pattern.trim()) {
      setServerError('A match pattern is required.'); return;
    }
    if (!values.block_all_events && values.event_codes.length === 0 && values.event_group_ids.length === 0) {
      setServerError('Select event codes or groups, or enable "Block all events".'); return;
    }
    if (!isEdit && scope === 'tenant' && !tenant) { setServerError('Pick a tour operator (or switch scope to System).'); return; }
    mutation.mutate(values);
  });

  const loading = isEdit && ruleQuery.isLoading;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit blacklist rule' : 'New blacklist rule'}</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3 }}>
        <Tab label="Editor" />
        {isEdit && <Tab label="Change history" />}
      </Tabs>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : tab === 0 ? (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {!isEdit && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField select size="small" label="Scope" value={scope} sx={{ width: 140 }}
                    onChange={(e) => setScope(e.target.value as 'system' | 'tenant')}>
                    <MenuItem value="system">System</MenuItem>
                    <MenuItem value="tenant">Tenant</MenuItem>
                  </TextField>
                  {scope === 'tenant' && (
                    <Box sx={{ flexGrow: 1 }}>
                      <TenantPicker
                        label="Tour operator"
                        value={tenant}
                        featured={pageTenants}
                        onChange={setTenant}
                      />
                    </Box>
                  )}
                </Stack>
              )}

              <Controller control={control} name="delivery_type" render={({ field }) => (
                <TextField select label="Delivery type" {...field} fullWidth>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="airbnb">Airbnb</MenuItem>
                </TextField>
              )} />

              <Controller control={control} name="match_type" render={({ field }) => (
                <TextField select label="Match type" {...field} fullWidth>
                  <MenuItem value="exact">Exact match</MenuItem>
                  <MenuItem value="substring">Substring match</MenuItem>
                  <MenuItem value="regex">Regular expression</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="ota">OTA</MenuItem>
                  <MenuItem value="reseller">Reseller</MenuItem>
                  <MenuItem value="tag">Tag condition</MenuItem>
                </TextField>
              )} />

              {matchType === 'user' && (
                <Box>
                  <TextField label="User id" type="number" fullWidth
                    error={Boolean(errors.user)} helperText="Numeric id of the user to block"
                    {...register('user')} />
                  {/* An id says nothing; the saved rule knows whose it is. */}
                  {savedUser && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                      <ValueActions actions={ruleUserActions(savedUser, { tenantId: ruleTenantId })}>
                        {savedUser.email ?? savedUser.name ?? `#${savedUser.id}`}
                      </ValueActions>
                    </Typography>
                  )}
                </Box>
              )}
              {matchType === 'ota' && (
                <RemoteAutocomplete<OtaBrief>
                  fetchOnEmpty
                  queryFn={(q) => blacklistsApi.otas(q)}
                  getOptionLabel={(o) => o.name}
                  getOptionKey={(o) => o.id}
                  value={ota}
                  onChange={(v) => setOta(v as OtaBrief | null)}
                  label="Online travel agency"
                  placeholder="Search OTA…"
                />
              )}
              {matchType === 'ota' && ota && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -1 }}>
                  <ValueActions actions={ruleOtaActions(ota)}>{ota.name}</ValueActions>
                </Typography>
              )}
              {matchType === 'tag' && (
                <>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">Tag condition</Typography>
                      <FieldHelp help={TAG_HELP.condition} />
                    </Stack>
                    <TagConditionInput
                      value={tagCondition}
                      onChange={setTagCondition}
                      fetchers={TAG_FETCHERS}
                      helperText="Blocks EVERY destination of a booking whose context carries these tags."
                    />
                  </Box>
                  <Stack direction="row" alignItems="flex-start" spacing={0.25}>
                    <TagSourcesSelect
                      value={tagSources}
                      onChange={setTagSources}
                      options={tagRegistryQuery.data?.tag_sources ?? []}
                      label="Tag sources"
                      helperText="The pool is the union of these — a wider selection blocks more."
                    />
                    <Box sx={{ pt: 1.5 }}><FieldHelp help={TAG_HELP.sources} /></Box>
                  </Stack>
                  {scope === 'system' && tagCondition.chips.some((c) => 'tag_id' in c.spec) && (
                    <Alert severity="info">
                      A tag pinned to one tenant (#) can only ever match that tenant. Use a
                      category&nbsp;code&nbsp;+&nbsp;tag&nbsp;name chip for a rule that should
                      apply across tenants.
                    </Alert>
                  )}
                </>
              )}
              {matchType !== 'user' && matchType !== 'ota' && matchType !== 'tag' && (
                <TextField label={matchType === 'reseller' ? 'Reseller name' : 'Match pattern'} fullWidth
                  error={Boolean(errors.pattern)}
                  helperText={matchType === 'regex'
                    ? 'Regular expression matched against the destination'
                    : matchType === 'reseller'
                      ? 'Reseller tag name (exact, case-insensitive)'
                      : 'Matched against the destination'}
                  {...register('pattern')} />
              )}

              <Controller control={control} name="is_active" render={({ field }) => (
                <FormControlLabel label="Active"
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
              )} />

              <Controller control={control} name="block_all_events" render={({ field }) => (
                <FormControlLabel label="Block all events"
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
              )} />

              {!blockAll && (
                <>
                  <Controller control={control} name="event_group_ids" render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="bl-groups">Event groups</InputLabel>
                      <Select labelId="bl-groups" multiple label="Event groups" value={field.value}
                        onChange={(e) => field.onChange(
                          typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : (e.target.value as number[]),
                        )}
                        renderValue={(sel) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(sel as number[]).map((id) => {
                              const g = groupsQuery.data?.results.find((x) => x.id === id);
                              return <Chip key={id} label={g?.name ?? `#${id}`} size="small" />;
                            })}
                          </Box>
                        )}>
                        {groupsQuery.data?.results.map((g) => (
                          <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )} />

                  <Controller control={control} name="event_codes" render={({ field }) => (
                    <Autocomplete multiple options={codesQuery.data ?? []} value={field.value}
                      onChange={(_, v) => field.onChange(v)}
                      renderInput={(params) => <TextField {...params} label="Event codes" placeholder="Add event code" />} />
                  )} />
                </>
              )}

              <CollapsibleSection title="Exceptions (never blocked)" summary={exceptionsSummary}>
                <Controller control={control} name="exception_event_group_ids" render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="bl-exc-groups">Exception event groups</InputLabel>
                    <Select labelId="bl-exc-groups" multiple label="Exception event groups" value={field.value}
                      onChange={(e) => field.onChange(
                        typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : (e.target.value as number[]),
                      )}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as number[]).map((id) => {
                            const g = groupsQuery.data?.results.find((x) => x.id === id);
                            return <Chip key={id} label={g?.name ?? `#${id}`} size="small" />;
                          })}
                        </Box>
                      )}>
                      {groupsQuery.data?.results.map((g) => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />

                <Controller control={control} name="exception_event_codes" render={({ field }) => (
                  <Autocomplete multiple options={codesQuery.data ?? []} value={field.value}
                    onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => <TextField {...params} label="Exception event codes" placeholder="Add event code"
                      helperText="Events matching these are exempted even when blocked above" />} />
                )} />
              </CollapsibleSection>

              <CollapsibleSection title="Redirect (optional)" summary={redirectSummary}
                forceOpen={Boolean(errors.redirect_to_email || errors.redirect_reply_to_email)}>
                <TextField label="Redirect blocked deliveries to" fullWidth
                  error={Boolean(errors.redirect_to_email)}
                  helperText={errors.redirect_to_email?.message
                    ?? `Up to ${MAX_REDIRECT_INBOXES} email inboxes (comma-separated) that receive a copy of every delivery blocked by this rule (instead of the message being dropped silently)`}
                  InputProps={{ endAdornment: <FieldHelp help={REDIRECT_HELP.redirect_to} /> }}
                  {...register('redirect_to_email')} />

                {Boolean(redirectTo.trim()) && (
                  <>
                    <TextField label="Custom Reply-To email (optional)" fullWidth
                      error={Boolean(errors.redirect_reply_to_email)}
                      helperText={errors.redirect_reply_to_email?.message
                        ?? 'Replies from the redirect inbox(es) go to this address instead of into the guest conversation'}
                      InputProps={{ endAdornment: <FieldHelp help={REDIRECT_HELP.reply_to} /> }}
                      {...register('redirect_reply_to_email')} />

                    {/* A custom Reply-To supersedes conversation reply routing, so the
                        switch is hidden (it would be inert) while one is entered. */}
                    {!redirectReplyTo.trim() && (
                      <Controller control={control} name="redirect_append_conversation_sid" render={({ field }) => (
                        <FormControlLabel
                          label={labelWithHelp(
                            "Allow the inbox to reply into the guest's conversation (reply appears as the guest)",
                            REDIRECT_HELP.append_sid,
                          )}
                          control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
                      )} />
                    )}
                  </>
                )}
              </CollapsibleSection>

              <TextField label="Comment (why blocking — optional)" fullWidth multiline
                helperText="Shown in delivery logs when a delivery is blocked" {...register('comment')} />

              <TextField label="Reason for this change (required)" fullWidth required
                error={Boolean(errors.reason)} helperText={errors.reason?.message ?? 'Stored in the change history'}
                {...register('reason')} />

              {serverError && <Alert severity="error">{serverError}</Alert>}
            </Stack>
          </Box>
        ) : (
          <RuleHistory ruleId={ruleId as number} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tab === 0 && (
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting || mutation.isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function RuleHistory({ ruleId }: { ruleId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['consoleBlacklistRuleHistory', ruleId],
    queryFn: () => blacklistsApi.history(ruleId, { page: 1, page_size: 200 }),
  });
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;
  const rows = data?.results ?? [];
  if (rows.length === 0) return <Box sx={{ p: 2 }}>No change history yet.</Box>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>When</TableCell>
          <TableCell>Action</TableCell>
          <TableCell>By</TableCell>
          <TableCell>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell>{r.created_ts.replace('T', ' ').slice(0, 16)}</TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>{r.action}</TableCell>
            <TableCell>
              {r.actor ? (
                <ValueActions actions={actorActions(r.actor)}>
                  {r.actor.email ?? r.actor.name ?? `#${r.actor.id}`}
                </ValueActions>
              ) : (
                '—'
              )}
            </TableCell>
            <TableCell>{r.reason || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
